import React, { useCallback, useEffect, useState } from "react";
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  UncontrolledAccordion,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../auth/service";
import {
  getCollections,
  createCollection,
  updateCollection,
  deleteCollection,
  deleteCollectionProduct,
} from "./collectionsService";

const CollectionsManager = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formIdentifier, setFormIdentifier] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      setNotification(null);
      const list = await getCollections();
      setCollections(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load collections:", err);
      const message =
        err?.error ??
        err?.message ??
        "Failed to load collections. Please try again.";
      setNotification({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    if (!user || user.isFromLandingPage) {
      navigate("/login_with_otp", { replace: true });
      return;
    }
    fetchCollections();
  }, [fetchCollections, navigate]);

  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormName("");
    setFormIdentifier("");
    setNotification(null);
    setIsModalOpen(true);
  };

  const openEditModal = (collection) => {
    setIsEditMode(true);
    setEditingId(collection?.id ?? null);
    setFormName(collection?.name ?? "");
    setFormIdentifier(collection?.identifier ?? "");
    setNotification(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormName("");
    setFormIdentifier("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formName?.trim();
    if (!name) {
      setNotification({ type: "danger", message: "Please enter a name." });
      return;
    }
    const identifier = formIdentifier?.trim() || null;
    try {
      setIsSubmitting(true);
      setNotification(null);
      if (isEditMode && editingId != null) {
        await updateCollection(editingId, name, identifier);
        setNotification({
          type: "success",
          message: "Collection updated successfully.",
        });
      } else {
        await createCollection(name, identifier);
        setNotification({
          type: "success",
          message: "Collection created successfully.",
        });
      }
      closeModal();
      fetchCollections();
    } catch (err) {
      const message =
        err?.error ??
        err?.message ??
        "Failed to save collection. Please try again.";
      setNotification({ type: "danger", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (collection) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${
        collection?.name ?? "this collection"
      }"?`
    );
    if (!confirmed) return;
    try {
      await deleteCollection(collection.id);
      setNotification({
        type: "success",
        message: "Collection deleted successfully.",
      });
      fetchCollections();
    } catch (err) {
      const message =
        err?.error ??
        err?.message ??
        "Failed to delete collection. Please try again.";
      setNotification({ type: "danger", message });
    }
  };

  const handleRemoveProduct = async (
    collectionProductId,
    productName,
    collectionName
  ) => {
    const confirmed = window.confirm(
      `Remove "${productName ?? "this product"}" from collection "${
        collectionName ?? "collection"
      }"?`
    );
    if (!confirmed) return;
    try {
      await deleteCollectionProduct(collectionProductId);
      setNotification({
        type: "success",
        message: "Product removed from collection.",
      });
      fetchCollections();
    } catch (err) {
      const message =
        err?.error ??
        err?.message ??
        "Failed to remove product from collection.";
      setNotification({ type: "danger", message });
    }
  };

  return (
    <div className="bg-light min-vh-100 py-4">
      <Container>
        <Row className="mb-4 align-items-center">
          <Col className="p-0">
            <h1 className="h3 mb-0">Manage collections</h1>
          </Col>
          <Col className="p-0" xs="auto">
            <Button color="primary" onClick={openCreateModal}>
              + Add collection
            </Button>
          </Col>
        </Row>

        {notification && (
          <div className={`alert alert-${notification.type}`} role="alert">
            {notification.message}
          </div>
        )}

        <div className="bg-white border rounded shadow-sm mt-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-3 mb-0 text-muted">Loading collections...</p>
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center py-5 px-3">
              <p className="text-muted mb-0">
                No collections yet. Add one to get started.
              </p>
            </div>
          ) : (
            <UncontrolledAccordion stayOpen className="mb-0">
              {collections.map((c) => {
                const accordionId = `collection-${c.id}`;
                return (
                  <AccordionItem key={c.id}>
                    <AccordionHeader targetId={accordionId}>
                      <span className="fw-semibold">{c.name ?? "-"}</span>
                      <div
                        className="ms-3 d-flex gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          onClick={() => openEditModal(c)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          outline
                          onClick={() => handleDelete(c)}
                        >
                          Delete
                        </Button>
                      </div>
                    </AccordionHeader>
                    <AccordionBody accordionId={accordionId}>
                      {(c.products || []).length === 0 ? (
                        <p className="text-muted small mb-0">
                          No products in this collection.
                        </p>
                      ) : (
                        <div className="d-flex flex-wrap gap-3 align-items-center">
                          {(c.products || []).map((p) => {
                            const cpId = p.collection_product_id ?? p.id;
                            const imgUrl = p.image ?? p.image_url;
                            const productName = `${p.name} (#${p.id})`;
                            return (
                              <div
                                key={cpId}
                                className="d-flex align-items-center gap-2 border rounded bg-white p-2"
                                style={{ minWidth: 180 }}
                              >
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={productName}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      objectFit: "contain",
                                      borderRadius: 4,
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="bg-secondary bg-opacity-25 rounded d-flex align-items-center justify-content-center text-muted small"
                                    style={{ width: 40, height: 40 }}
                                  >
                                    —
                                  </div>
                                )}
                                <span
                                  className="flex-grow-1 text-truncate small"
                                  title={productName}
                                >
                                  {productName}
                                </span>
                                <Button
                                  size="sm"
                                  color="danger"
                                  outline
                                  onClick={() =>
                                    handleRemoveProduct(
                                      cpId,
                                      productName,
                                      c.name
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </AccordionBody>
                  </AccordionItem>
                );
              })}
            </UncontrolledAccordion>
          )}
        </div>
      </Container>

      <Modal isOpen={isModalOpen} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>
          {isEditMode ? "Edit collection" : "Add collection"}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="collection-name">Name</Label>
              <Input
                id="collection-name"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Collection name"
                autoFocus
                className="unmask"
              />
            </FormGroup>
            <FormGroup>
              <Label for="collection-identifier">Identifier</Label>
              <Input
                id="collection-identifier"
                type="text"
                value={formIdentifier}
                onChange={(e) => setFormIdentifier(e.target.value)}
                placeholder="URL identifier (e.g. spring-sale)"
                className="unmask"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="link" onClick={closeModal} type="button">
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : isEditMode ? (
                "Save changes"
              ) : (
                "Create collection"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default CollectionsManager;
